import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  CalendarCheck, User, Clock, Building2, DoorOpen, Hospital, Bell, FileSignature,
  Mail, MessageSquare, Phone
} from "lucide-react";
import { servicesData, serviceBundlesData } from "@/data/mockData";
import { getPharmacyById, getRoomById, getStructureById, urgencyLevels, totalServicePrice } from "@/data/platformData";
import { MDRBadge } from "@/components/MDRBadge";

export const BookingConfirmStep = ({ booking, initiator }) => {
  const pharmacy = getPharmacyById(booking.pharmacyId);
  const room = getRoomById(booking.diagnosticRoomId);
  const structure = booking.structureId ? getStructureById(booking.structureId) : null;
  const urgency = urgencyLevels.find(u => u.id === booking.urgency);
  const bundle = booking.bundleId ? serviceBundlesData.find(b => b.id === booking.bundleId) : null;
  const total = totalServicePrice(booking.serviceIds);
  const hasReporting = booking.serviceIds.some(sid => servicesData.find(s => s.id === sid)?.requiresReport);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Riepilogo e conferma</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Verifica i dettagli della prenotazione prima di confermare.
        </p>
      </div>

      <Card className="p-5 space-y-4 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        {/* Patient */}
        <div className="flex items-start gap-3">
          <User className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Paziente</p>
            <p className="font-medium">{booking.patient?.name}</p>
            <p className="text-xs text-muted-foreground font-mono">{booking.patient?.fiscalCode}</p>
          </div>
        </div>

        <Separator />

        {/* Date & time */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-start gap-3">
            <CalendarCheck className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Data</p>
              <p className="font-medium">{new Date(booking.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Ora</p>
              <p className="font-medium">{booking.time}</p>
              <Badge variant="outline" className="mt-1 text-[10px]">{urgency?.label}</Badge>
            </div>
          </div>
        </div>

        <Separator />

        {/* Pharmacy & Room */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Farmacia</p>
              <p className="font-medium">{pharmacy?.name}</p>
              <p className="text-xs text-muted-foreground">{pharmacy?.address}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <DoorOpen className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Sala</p>
              <p className="font-medium">{room?.name}</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Services */}
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Prestazioni{bundle && ` · Profilo "${bundle.name}"`}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {booking.serviceIds.map(sid => {
              const s = servicesData.find(x => x.id === sid);
              return (
                <Badge key={sid} variant="secondary" className="gap-1">
                  {s?.name}
                  {s?.requiresReport && <FileSignature className="h-2.5 w-2.5 text-rose-600" />}
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Routing to structure */}
        {structure && (
          <>
            <Separator />
            <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200">
              <Hospital className="h-5 w-5 text-rose-600 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs uppercase tracking-wider text-rose-700 font-semibold">Struttura accreditata di destinazione</p>
                  <MDRBadge variant="medical" size="sm" />
                </div>
                <p className="font-medium text-rose-900 dark:text-rose-200 mt-0.5">{structure.name}</p>
                <p className="text-xs text-rose-700/80">Riceve placeholder esame per refertazione asincrona</p>
              </div>
            </div>
          </>
        )}

        {/* Notes */}
        {booking.notes && (
          <>
            <Separator />
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Note</p>
              <p className="text-sm">{booking.notes}</p>
            </div>
          </>
        )}

        <Separator />

        {/* Total */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Totale stimato</span>
          <span className="text-2xl font-bold text-primary">€{total.toFixed(2)}</span>
        </div>
      </Card>

      {/* Reminders panel */}
      <Card className="p-4 border-dashed bg-muted/30">
        <div className="flex items-start gap-3">
          <Bell className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-2 flex-1">
            <p className="text-sm font-semibold">Reminder automatici</p>
            <p className="text-xs text-muted-foreground">Alla conferma verranno inviati al paziente:</p>
            <div className="flex flex-wrap gap-2 mt-1">
              <Badge variant="outline" className="gap-1 text-xs">
                <Mail className="h-3 w-3" /> Email immediata
              </Badge>
              <Badge variant="outline" className="gap-1 text-xs">
                <MessageSquare className="h-3 w-3" /> SMS reminder T-24h
              </Badge>
              <Badge variant="outline" className="gap-1 text-xs">
                <Phone className="h-3 w-3" /> Notifica push T-1h
              </Badge>
            </div>
            {hasReporting && (
              <p className="text-xs text-muted-foreground mt-2">
                <strong>Placeholder</strong> inviato alla struttura accreditata per la presa in carico della refertazione.
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BookingConfirmStep;
